import React, { useState } from 'react';
import Dashboard from './screens/Dashboard';
import Historico from './screens/Historico';
import LoginScreen from './screens/LoginScreen';
import NovoAtendimento from './screens/NovoAtendimento';
import Prestadores from './screens/Prestadores';
import Usuarios from './screens/Usuarios';
import LayoutShell from './components/layout/LayoutShell';
import {
  clearSession,
  createApiFetch,
  getStoredToken,
  getStoredUsuario,
  loginRequest,
  saveSession,
} from './utils/auth';
import {
  getAbaAtualSegura,
  getNomePerfil,
  getPermissoes,
} from './utils/permissions';
import { buscarDadosAtendimento } from './utils/atendimento';

const API_URL = '/api';

function App() {
  const [abaAtiva, setAbaAtiva] = useState('novo');
  const [cpfBusca, setCpfBusca] = useState('');
  const [associado, setAssociado] = useState(null);
  const [situacaoFinanceira, setSituacaoFinanceira] = useState(null);
  const [boletosPendentes, setBoletosPendentes] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState(null);

  const [token, setToken] = useState(() => getStoredToken());
  const [usuarioLogado, setUsuarioLogado] = useState(() => getStoredUsuario());

  const [carregandoLogin, setCarregandoLogin] = useState(false);
  const [erroLogin, setErroLogin] = useState(null);

  const limparSessao = () => {
    setToken('');
    setUsuarioLogado(null);
    setCpfBusca('');
    setAssociado(null);
    setSituacaoFinanceira(null);
    setBoletosPendentes(null);
    setErro(null);
    setErroLogin(null);
    setAbaAtiva('novo');
    clearSession();
  };

  const apiFetch = createApiFetch(token, limparSessao);

  const fazerLogin = async (email, senha) => {
    try {
      setCarregandoLogin(true);
      setErroLogin(null);

      const data = await loginRequest(API_URL, email, senha);

      setToken(data.token);
      setUsuarioLogado(data.usuario);
      saveSession(data.token, data.usuario);
    } catch (err) {
      setErroLogin('Erro ao entrar: ' + err.message);
    } finally {
      setCarregandoLogin(false);
    }
  };

  const sair = () => {
    limparSessao();
  };

  const buscarAssociado = async () => {
    if (!cpfBusca) return;

    setCarregando(true);
    setErro(null);
    setAssociado(null);
    setSituacaoFinanceira(null);
    setBoletosPendentes(null);

    try {
      const dados = await buscarDadosAtendimento(apiFetch, cpfBusca);

      setAssociado(dados.associado);
      setSituacaoFinanceira(dados.situacaoFinanceira);
      setBoletosPendentes(dados.boletosPendentes);
    } catch (err) {
      if (err.message?.includes('sessão')) {
        setErroLogin(err.message);
      } else if (err.message?.includes('permissão')) {
        setErro(err.message);
      } else {
        setErro('Erro ao buscar: ' + err.message);
      }
    } finally {
      setCarregando(false);
    }
  };

  if (!token || !usuarioLogado) {
    return (
      <LoginScreen
        fazerLogin={fazerLogin}
        carregandoLogin={carregandoLogin}
        erroLogin={erroLogin}
      />
    );
  }

  const perfil = usuarioLogado?.perfil || '';
  const nomePerfil = getNomePerfil(perfil);
  const permissoes = getPermissoes(perfil);
  const abaAtualSegura = getAbaAtualSegura(abaAtiva, permissoes);

  return (
    <LayoutShell
      abaAtiva={abaAtualSegura}
      setAbaAtiva={setAbaAtiva}
      permissoes={permissoes}
      usuario={usuarioLogado}
      nomePerfil={nomePerfil}
      onLogout={sair}
    >
      {abaAtualSegura === 'novo' && permissoes.podeVerNovoAtendimento && (
        <NovoAtendimento
          cpfBusca={cpfBusca}
          setCpfBusca={setCpfBusca}
          buscarAssociado={buscarAssociado}
          associado={associado}
          situacaoFinanceira={situacaoFinanceira}
          boletosPendentes={boletosPendentes}
          carregando={carregando}
          erro={erro}
          podeOperarAtendimento={permissoes.podeOperarAtendimento}
          apiFetch={apiFetch}
        />
      )}

      {abaAtualSegura === 'dashboard' && permissoes.podeVerDashboard && (
        <Dashboard
          perfilLabel={nomePerfil}
          podeVerRelatorios={permissoes.podeVerRelatorios}
          podeAdministrarUsuarios={permissoes.podeAdministrarUsuarios}
          podeOperarAtendimento={permissoes.podeOperarAtendimento}
          podeGerenciarPrestadores={permissoes.podeGerenciarPrestadores}
          apiFetch={apiFetch}
          setAbaAtiva={setAbaAtiva}
        />
      )}

      {abaAtualSegura === 'prestadores' && permissoes.podeVerPrestadores && (
        <Prestadores
          apiFetch={apiFetch}
          podeGerenciarPrestadores={permissoes.podeGerenciarPrestadores}
        />
      )}

      {abaAtualSegura === 'historico' && permissoes.podeVerHistorico && (
        <Historico
          apiFetch={apiFetch}
          podeVerRelatorios={permissoes.podeVerRelatorios}
        />
      )}

      {abaAtualSegura === 'usuarios' && permissoes.podeVerUsuarios && (
        <Usuarios
          apiFetch={apiFetch}
          usuarioLogado={usuarioLogado}
        />
      )}
    </LayoutShell>
  );
}

export default App;
